// src/components/CreneauxSelector.js
import React, { useMemo, useState, useEffect } from "react";
import { API_BASE } from "../../config";
import { useParams } from "react-router-dom";
import Calendar from "react-calendar";
import { CalendarStyle } from "../../style/Calendrier";
import axios from "axios";
import { format, addMinutes, setHours, setMinutes } from "date-fns";
import { Dialog, DialogPanel, DialogBackdrop, DialogTitle } from "@headlessui/react";
import "react-calendar/dist/Calendar.css";
import { CheckCircleIcon, XMarkIcon } from "@heroicons/react/16/solid";
import { CalendarIcon } from "@heroicons/react/20/solid";

function classNames() {
  return Array.prototype.slice.call(arguments).filter(Boolean).join(" ");
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toTimeHHmm(date) {
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

function labelFromRange(date, start, end) {
  const dateStr = date.toLocaleDateString("fr-FR");
  return `${dateStr} - ${start} à ${end}`;
}

function parseHHmm(hhmm) {
  const parts = String(hhmm).split(":").map(Number);
  if (parts.length !== 2) return null;
  const h = parts[0];
  const m = parts[1];
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return { h, m };
}

function rangesOverlap(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

export default function CreneauxSelector(props) {
  const {
    id_transaction,
    type,
    refreshData,
    id_rdv,
    where,
    notaireAirtableId,
    prenomNotaire,
    nomNotaire,
    emailUser,
  } = props;

  const params = useParams();
  const transactionId = id_transaction != null ? id_transaction : params.id;

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);

  // On garde le fetch slots (utile plus tard), mais on n'affiche pas de mode agenda.
  const [slots, setSlots] = useState([]);

  // { startISO, endISO, label }
  const [selectedCreneaux, setSelectedCreneaux] = useState([]);

  // UI
  const [rendezvousType, setRendezvousType] = useState("Visio");
  const [successMessage, setSuccessMessage] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Durées
  const [durationMin, setDurationMin] = useState(30);

  // 08:00 -> 20:00, pas 30 min
  const START_MINUTES = 8 * 60;
  const END_MINUTES = 20 * 60;
  const STEP_MINUTES = 30;

  const normalizedNotaireId = useMemo(() => {
  if (!notaireAirtableId) return "";
  return Array.isArray(notaireAirtableId) ? String(notaireAirtableId[0] || "") : String(notaireAirtableId);
}, [notaireAirtableId]);

  // fetch slots (optionnel)
useEffect(() => {
  if (!normalizedNotaireId) {
    if (!selectedDate) setSelectedDate(new Date());
    return;
  }

  let cancelled = false;

  axios
    .get(`${API_BASE}/api/slots/${normalizedNotaireId}`)
    .then((res) => {
      if (cancelled) return;
      const fetched = res?.data?.slots ? res.data.slots : [];
      setSlots(fetched);

      // ⚠️ important: utiliser la version "fonction" pour éviter le stale closure
      setSelectedDate((prev) => prev || new Date());
    })
    .catch((err) => {
      if (cancelled) return;
      console.error("❌ Erreur lors du chargement des créneaux:", err);
      setSelectedDate((prev) => prev || new Date());
    });

  return () => {
    cancelled = true;
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [normalizedNotaireId]);

  const manualStarts = useMemo(() => {
    if (!selectedDate) return [];
    const starts = [];
    const endMax = END_MINUTES - durationMin;

    for (let m = START_MINUTES; m <= endMax; m += STEP_MINUTES) {
      const h = Math.floor(m / 60);
      const mm = m % 60;
      starts.push(`${pad2(h)}:${pad2(mm)}`);
    }
    return starts;
  }, [selectedDate, durationMin]);

  const addCreneauObject = (dateObj, startDate, endDate) => {
    if (!dateObj || !startDate || !endDate) return;
    if (selectedCreneaux.length >= 3) return;

    const startISO = startDate.toISOString();
    const endISO = endDate.toISOString();

    // anti-doublon
    for (let i = 0; i < selectedCreneaux.length; i += 1) {
      const c = selectedCreneaux[i];
      if (c.startISO === startISO && c.endISO === endISO) return;
    }

    // anti-chevauchement
    for (let j = 0; j < selectedCreneaux.length; j += 1) {
      const s = selectedCreneaux[j];
      const overlap = rangesOverlap(
        new Date(s.startISO),
        new Date(s.endISO),
        startDate,
        endDate
      );
      if (overlap) {
        alert("Ce créneau chevauche un autre créneau déjà sélectionné.");
        return;
      }
    }

    const label = labelFromRange(dateObj, toTimeHHmm(startDate), toTimeHHmm(endDate));
    setSelectedCreneaux(function (prev) {
      return prev.concat([{ startISO: startISO, endISO: endISO, label: label }]);
    });
  };

  const addManualFromGrid = (hhmm) => {
    if (!selectedDate) return;
    const parsed = parseHHmm(hhmm);
    if (!parsed) return;

    const start = setMinutes(setHours(new Date(selectedDate), parsed.h), parsed.m);
    const end = addMinutes(start, durationMin);

    addCreneauObject(selectedDate, start, end);
  };

  const removeCreneau = (idx) => {
    setSelectedCreneaux(function (prev) {
      return prev.filter(function (_, i) {
        return i !== idx;
      });
    });
  };

  const payloadCreneauxStrings = useMemo(() => {
    return selectedCreneaux.map(function (c) {
      return c.label;
    });
  }, [selectedCreneaux]);

  const confirmSelection = async () => {
    if (!transactionId) {
      alert("Transaction inconnue.");
      return;
    }
    if (payloadCreneauxStrings.length === 0) return;

    const payload = {
  prenomNotaire: prenomNotaire,
  nomNotaire: nomNotaire,
  emailUser: emailUser,

  transaction_id: transactionId,
  id_rdv: id_rdv,

  notaireAirtableId: normalizedNotaireId,

  "creneaux[]": payloadCreneauxStrings,
  type: rendezvousType,
  type_demarche: type,
  date_proposition: new Date().toISOString(),
};

    setIsSubmitting(true);

    try {
      //https://cloterenotaire.onrender.com/
        //http://localhost:5050/
      const res = await fetch(`${API_BASE}/api/propose-meetings`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const txt = await res.text();
        throw new Error(`HTTP ${res.status} - ${txt}`);
      }

      // ✅ UX: success flash très court + fermeture auto + refresh
      setSuccessMessage(true);

      // ferme et refresh tout de suite (pas besoin d'attendre 2s)
      setIsDrawerOpen(false);
      refreshData && refreshData();

      // reset states
      setSelectedCreneaux([]);
      setDurationMin(30);

      // petit flash (si tu veux)
      setTimeout(function () {
        setSuccessMessage(false);
      }, 600);
    } catch (e) {
      console.error("Erreur propose-meetings :", e);
      alert("Une erreur est survenue, veuillez réessayer.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDrawer = () => {
    setIsDrawerOpen(true);
    if (!selectedDate) setSelectedDate(new Date());
  };

  const typeLocked = selectedCreneaux.length > 0;

  console.log("notaireAirtableId", notaireAirtableId);

  return (
    <>
      <button
        className="btn-main mb-0"
        onClick={openDrawer}
      >
        {where === "header" ? <CalendarIcon className="inline-flex size-6 mr-2 mb-0" /> : null}
        Proposer un rendez-vous
      </button>

      <Dialog open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} className="relative z-10">
        <DialogBackdrop className="fixed inset-0 bg-gray-500 bg-opacity-75" />

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <DialogPanel className="pointer-events-auto w-screen max-w-md transform bg-white shadow-xl">
                <div className="flex h-full flex-col py-3">
                  <div className="px-4 sm:px-6 flex items-start justify-between">
                    <DialogTitle className="text-lg font-medium text-gray-900">
                      Proposez jusqu’à 3 créneaux
                    </DialogTitle>

                    <button
                      onClick={() => setIsDrawerOpen(false)}
                      className="text-gray-400 hover:text-gray-500"
                    >
                      <XMarkIcon className="size-6" />
                    </button>
                  </div>

                  <div className="relative mt-4 flex-1 px-4 sm:px-6 overflow-y-auto">
                    {successMessage ? (
                      <div className="text-left flex flex-row gap-2 items-center mb-8">
                        <CheckCircleIcon className="mx-auto size-8 text-emerald-600" />
                        <p className="mt-2 text-sm font-bold text-emerald-600 mb-0">
                          Envoyé ✔
                        </p>
                      </div>
                    ) : (
                      <>
                        {/* Type rendez-vous (verrouillé dès qu'on a 1 créneau) */}
                        <div className="mb-4">
                          <p className="text-sm font-medium text-gray-900">Type de rendez-vous :</p>
                          <div className="mt-2 flex gap-2 flex-wrap">
                            {["Visio", "A l'étude", "Téléphone"].map(function (m) {
                              return (
                                <button
                                  key={m}
                                  onClick={() => {
                                    if (typeLocked) return;
                                    setRendezvousType(m);
                                  }}
                                  disabled={typeLocked}
                                  className={classNames(
                                    "px-3 py-1 rounded-md border",
                                    rendezvousType === m
                                      ? "bg-indigo-600 text-white"
                                      : "bg-gray-100 text-gray-700 hover:bg-gray-200",
                                    typeLocked ? "opacity-50 cursor-not-allowed" : ""
                                  )}
                                  title={typeLocked ? "Videz la sélection pour changer le type" : ""}
                                >
                                  {m}
                                </button>
                              );
                            })}
                          </div>
                        </div>

                        {/* Calendrier */}
                        <CalendarStyle>
                          <Calendar
                            onClickDay={(val) => setSelectedDate(val)}
                            value={selectedDate}
                            tileClassName={({ date }) => {
                              const isSelected =
                                selectedDate &&
                                format(selectedDate, "yyyy-MM-dd") === format(date, "yyyy-MM-dd");
                              return isSelected ? "text-white rounded !bg-indigo-600" : "bg-white text-gray-900";
                            }}
                            className="border rounded-lg shadow p-2 h-[100%]"
                          />
                        </CalendarStyle>

                        {/* Durée + choix horaire */}
                        {selectedDate && selectedCreneaux.length < 3 ? (
                          <div className="mt-6">
                            <p className="text-sm font-medium text-gray-900">Durée du rendez-vous</p>

                            <div className="mt-2 flex gap-2 flex-wrap">
                              {[
                                { label: "30 min", value: 30 },
                                { label: "45 min", value: 45 },
                                { label: "1 h", value: 60 },
                                { label: "1 h 30", value: 90 },
                                { label: "2 h", value: 120 },
                              ].map(function (opt) {
                                return (
                                  <button
                                    key={opt.value}
                                    onClick={() => setDurationMin(opt.value)}
                                    className={classNames(
                                      "px-3 py-1 rounded-md border text-sm",
                                      durationMin === opt.value
                                        ? "bg-indigo-600 text-white border-indigo-600"
                                        : "bg-white text-gray-700 hover:bg-gray-50"
                                    )}
                                  >
                                    {opt.label}
                                  </button>
                                );
                              })}
                            </div>

                            <p className="text-sm font-medium text-gray-900 mt-6">Choisissez un horaire</p>

                            <div className="mt-3 grid grid-cols-3 gap-2 mb-8">
                              {manualStarts.map(function (hhmm) {
                                return (
                                  <button
                                    key={hhmm}
                                    onClick={() => addManualFromGrid(hhmm)}
                                    className="p-2 rounded border border-gray-300 hover:bg-gray-900 hover:text-white text-sm"
                                  >
                                    {hhmm}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        ) : null}
                      </>
                    )}
                  </div>

                  <div className="px-4 sm:px-6 border-t-2 border-gray-200 py-2">
                    {selectedCreneaux.length > 0 ? (
                      <div className="mb-3">
                        <p className="text-sm font-medium text-gray-900 mt-2">Créneaux sélectionnés :</p>
                        <ul className="mt-2 space-y-2">
                          {selectedCreneaux.map(function (c, i) {
                            return (
                              <li
                                key={`${c.startISO}-${c.endISO}`}
                                className="flex justify-between items-center bg-gray-100 px-3 py-2 rounded"
                              >
                                <span className="text-sm">{c.label}</span>

                                <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-gray-500/10 ring-inset">
                                  {rendezvousType}
                                </span>

                                <button
                                  onClick={() => removeCreneau(i)}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  ✕
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    ) : null}

                    <button
                      onClick={confirmSelection}
                      disabled={selectedCreneaux.length === 0 || isSubmitting}
                      className={classNames(
                        "w-full py-2 rounded font-semibold",
                        selectedCreneaux.length === 0 || isSubmitting
                          ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                          : "bg-indigo-600 text-white hover:bg-indigo-700"
                      )}
                    >
                      {isSubmitting ? "Envoi..." : "Confirmer les créneaux"}
                    </button>
                  </div>
                </div>
              </DialogPanel>
            </div>
          </div>
        </div>
      </Dialog>
    </>
  );
}